import { z } from "zod";

import { AssetType } from "@chainpatrol/database/enums";

export enum ReviewView {
  Global = "global",
  Organization = "organization",
}

export const Events = {
  "web:takedown_drawer_open": z.object({
    takedown_id: z.number().optional(),
    takedown_status: z.string(),
  }),
  "web:takedown_email_send": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_retraction_email_send": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_email_cancel": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_retraction_email_cancel": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_email_content_copy": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:retraction_email_content_copy": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_gmail_redirect": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:retraction_gmail_redirect": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_mark_as_complete": z.object({
    takedown_id: z.number().nullable(),
    user_id: z.number().optional(),
  }),
  "web:takedown_unassign_user": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_assign_user": z.object({
    takedown_id: z.number().nullable(),
    assigned_user_id: z.number(),
  }),
  "web:takedown_action_click": z.object({
    takedown_id: z.number().nullable(),
    action: z.string(),
  }),
  "web:takedown_refile_done": z.object({
    takedown_id: z.number().nullable(),
  }),

  // Detection Config modal
  "web:detection_config_modal_generate_config": z.object({
    user_id: z.number().optional(),
  }),
  "web:detection_config_modal_generate_config_discard": z.object({
    user_id: z.number().optional(),
  }),
  "web:detection_config_modal_generate_config_apply": z.object({
    user_id: z.number().optional(),
  }),

  // Threats page
  "web:threats_filters_apply": z.object({
    filters: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        options: z.object({}),
        values: z.array(z.string()).optional(),
      }),
    ),
  }),

  // Staff page
  "web:staff_threats_redirect": z.object({
    user_id: z.number().optional(),
    slug: z.string(),
  }),
  "web:staff_filter_needs_review": z.object({
    user_id: z.number().optional(),
  }),
  "web:staff_filter_no_reporting_wave": z.object({
    user_id: z.number().optional(),
  }),
  "web:staff_view_change": z.object({
    user_id: z.number().optional(),
    view: z.enum(["all", "detection", "reporting", "reviewing", "takedowns"]),
  }),
  "api:key_used": z.object({
    user_id: z.string().optional(),
    path: z.string(),
    is_legacy: z.boolean().optional(),
    params: z.object({
      query: z.object({}),
      body: z.object({}),
    }),
  }),
  // Review Proposal
  "web:review_approve": z.object({
    organization_id: z.number(),
    report_id: z.number(),
    proposal_id: z.number(),
    view: z.enum(["global", "organization"]),
    reason: z.string(),
  }),
  "web:review_reject": z.object({
    organization_id: z.number(),
    report_id: z.number(),
    proposal_id: z.number(),
    view: z.enum(["global", "organization"]),
  }),
  "web:watchlist_toggle": z.object({
    organization_id: z.number(),
    report_id: z.number(),
    proposal_id: z.number(),
    view: z.enum(["global", "organization"]),
  }),
  "web:false_positive_flag": z.object({
    organization_id: z.number(),
    report_id: z.number(),
    proposal_id: z.number(),
    view: z.enum(["global", "organization"]),
    reason: z.string(),
  }),

  // Global Reviewing Page
  "web:review_organization_sidebar_collapse": z.object({
    view: z.enum(["closed", "open"]),
  }),

  "web:review_org_sort_method": z.object({
    view: z.enum(["alphabetic", "count", "age"]),
  }),

  "web:review_org_type": z.object({
    types: z.array(z.enum(["customer", "hot_lead", "prospect", "none"])),
  }),

  "web:review_proposal_view": z.object({
    view: z.enum(["all", "organization"]),
    date: z.enum(["24h", "7d", "30d", "all"]).optional(),
    slug: z.string().optional(),
  }),

  "web:review_search_box": z.object({
    // send only when they click on something else, aka blur
    content: z.string(),
  }),

  "web:review_proposal_queue_type": z.object({
    view: z.enum(["all", "priority", "watchlist"]),
  }),

  "web:review_proposal_filters": z.object({
    included: z.boolean(),
    asset_type: z.array(z.nativeEnum(AssetType)).optional(),
    asset_scan_label: z.array(z.string()).optional(),
  }),

  "web:review_automation_toggle": z.object({
    automation: z.enum(["enabled", "disabled"]),
  }),

  "web:review_proposal_settings_toggle": z.object({
    view: z.enum(["enabled", "disabled"]),
  }),

  "web:review_focus_mode_toggle": z.object({
    view: z.enum(["enabled", "disabled"]),
  }),

  "web:legitimacy_check_shown": z.object({
    organization_id: z.number(),
    proposal_id: z.number(),
    report_id: z.number(),
    asset_content: z.string(),
    legitimacy_sources: z.array(z.string()),
  }),

  "web:legitimacy_check_bypassed": z.object({
    organization_id: z.number(),
    proposal_id: z.number(),
    report_id: z.number(),
    asset_content: z.string(),
    legitimacy_sources: z.array(z.string()),
    bypass_method: z.enum(["checkbox", "acknowledge_input"]),
  }),

  // Takedown Drawer
  "web:takedown_start": z.object({
    organization_id: z.number(),
    asset_id: z.number(),
    asset_type: z.string(),
    takedown_id: z.number(),
    takedown_assignee_id: z.number(),
  }),
  "web:takedown_cancel": z.object({
    organization_id: z.number(),
    asset_id: z.number(),
    asset_type: z.string(),
    takedown_id: z.number(),
    takedown_assignee_id: z.number(),
  }),
  "web:takedown_refresh_click": z.object({
    takedown_id: z.number().nullable(),
  }),
  "web:takedown_bulk_refresh_click": z.object({
    takedown_ids: z.array(z.number().nullable()),
  }),

  // Detection Page
  "web:detection_result_select": z.object({
    organization_id: z.number(),
    detection_result_id: z.number(),
    source: z.string(),
  }),
  "web:detection_create_report": z.object({
    organization_id: z.number(),
    detection_result_ids: z.array(z.number()),
  }),

  "web:detection_copy_content": z.object({
    organization_id: z.number(),
    detection_result_id: z.number(),
    source: z.string(),
  }),

  "web:detection_filter_select": z.object({
    organization_id: z.number(),
    filter_group: z.string(),
    filter_option: z.string(),
    state: z.boolean(),
  }),

  "web:detection_filter_toggle": z.object({
    organization_id: z.number(),
    state: z.boolean(),
  }),

  // Threats page
  "web:threats_copy_to_clipboard": z.object({
    organization_id: z.number(),
    takedown_assignee_id: z.number(),
  }),

  // Snapshot Generation - Basic Usage Tracking
  "web:snapshot_dashboard_create_new": z.object({
    organization_ids: z.array(z.number()),
    primary_organization_id: z.number(),
  }),

  "web:snapshot_dashboard_open": z.object({
    snapshot_id: z.number(),
    organization_id: z.number(),
  }),

  "web:snapshot_editor_pdf_generate": z.object({
    snapshot_id: z.number(),
    organization_ids: z.array(z.number()),
  }),

  // Case Studies Editor - Detailed Tracking
  "web:snapshot_case_studies_add": z.object({
    snapshot_id: z.number(),
    method: z.enum(["manual", "threat_examples", "auto_select"]),
    organization_ids: z.array(z.number()),
  }),

  "web:snapshot_case_studies_edit": z.object({
    snapshot_id: z.number(),
    case_study_id: z.string(),
    has_images: z.boolean(),
    image_count: z.number(),
  }),

  "web:snapshot_case_studies_delete": z.object({
    snapshot_id: z.number(),
    case_study_id: z.string(),
  }),

  "web:snapshot_case_studies_auto_select": z.object({
    snapshot_id: z.number(),
    organization_id: z.number(),
    date_range_days: z.number().positive(),
    results_count: z.number().nonnegative(),
  }),

  "web:snapshot_case_studies_image_upload": z.object({
    snapshot_id: z.number(),
    case_study_id: z.string().optional(),
    upload_method: z.enum(["upload", "url"]),
  }),

  "web:snapshot_case_studies_reorder": z.object({
    snapshot_id: z.number(),
    direction: z.enum(["up", "down"]),
    from_index: z.number(),
    to_index: z.number(),
  }),

  "api:organization_created_immunefi": z.object({
    organization_id: z.number(),
    organization_slug: z.string(),
    organization_name: z.string(),
    is_test_mode: z.boolean(),
    api_key_environment: z.string(),
    created_via: z.literal("immunefi_endpoint"),
    has_website_url: z.boolean(),
    created_by_organization: z.string(),
  }),

  "api:organization_create_immunefi_request": z.object({
    organization_name: z.string(),
    organization_slug: z.string(),
  }),
};

export type EventName = keyof typeof Events;
